import { Vault } from "../../libs/storage.js";
import { ExtraParamsValues } from "./ExtraParams";
import { Scrub, ScrubRule } from "./Scrub";
import { Utils } from "./Utils";
import {
  reportProtoCtxInvalid,
  reportStorageFallback,
  reportWhopProtoCtxMissing,
} from "./CoreReport";

export {
  Tracking,
  UserData,
  UTMValues,
  StoredUTMValues,
  parseProtoCtx,
  buildProtoCtx,
  readProtoCtxExtension,
  ProtoCtxParseResult,
  PROTOCTX_PARAM,
  PROTOCTX_SEPARATOR,
  PROTOCTX_MAX_LENGTH,
  PROTOCTX_MARKER,
  WHOP_MARKER,
  PROTOCTX_VID_KEY,
  PROTOCTX_VIA_KEY,
};

interface UTMValues {
  utm_source: string | boolean;
  utm_campaign: string;
  utm_medium: string | boolean;
  utm_term: string;
  utm_content: string;
  utm_id?: string;
}

/**
 * Lo que se persiste en `t_utm_values`: los 6 UTM + `utm_via`, la PROCEDENCIA
 * de la atribución ("pctx" rescatada de protoctx, "whop" resuelta por la regla
 * de Whop Ads). `utm_via` es un marcador interno — nunca se envía como un
 * UTM más (getAdditionalDataToMessage destructura solo los 6); viaja aparte,
 * en `custom_data` del WebAnalyticsEvent. Se guarda junto a los UTM a
 * propósito: así el marcador sobrevive a la navegación por el funnel igual que
 * ellos y "rescatados" y "misatribución" se miden sobre el mismo universo de
 * pageviews.
 */
interface StoredUTMValues extends UTMValues {
  utm_via?: string;
}

interface UserData extends UTMValues, ExtraParamsValues {
  url: string;
  referrer: string;
  funnel_step: FunnelStepType;
  country_code?: string;
}

enum FunnelStepType {
  Landing = "landing",
  Webinar = "webinar",
  ThankYou = "thank_you",
  DirectBooking = "direct_booking",
}

/**
 * `protoctx` — UTMs empaquetadas en UN parámetro (rescate de atribución).
 *
 * El 8,3% de los clics de pago de Google llega al funnel CON auto-tagging
 * (gad_source/gclid/gbraid) pero SIN ningún `utm_*`: la superficie del clic
 * (Custom Tabs de Android, camino ITP de Safari) se los come y el visitante
 * acaba clasificado `org/direct/non-utm`. Las UTMs viajan en el final URL
 * suffix de Google Ads, así que el media buyer añade ahí también las mismas
 * UTMs empaquetadas bajo un nombre que esa superficie no conoce; si lo que
 * elimina los `utm_*` va parámetro a parámetro, `protoctx` sobrevive y las
 * recuperamos. (Si desaparece el sufijo entero, el rescate será ~0 — ese
 * resultado, medido con el marcador, decide el siguiente paso.)
 *
 * Contrato acordado con media buying — separador `~~` y ORDEN POSICIONAL
 * estricto, campos vacíos permitidos:
 *   utm_source ~~ utm_medium ~~ utm_campaign ~~ utm_content ~~ utm_term ~~ utm_id
 * Ejemplo real: `&protoctx=mig~~webinar_flow_b~~c{campaignid}~~a{creative}~~~~`
 *
 * El parser es agnóstico al canal: funcionará igual cuando Bing/Meta lo añadan
 * a sus sufijos.
 */
const PROTOCTX_PARAM = "protoctx";
const PROTOCTX_SEPARATOR = "~~";
// Guardarraíl, no mecanismo de corrección: por encima de esto no es nuestro
// contrato. Subido de 512 a 1024 (5/8/2026) al añadir las extensiones — lo
// GENERADO por decorateUrl se mantiene compacto (~250 chars) de todas formas.
const PROTOCTX_MAX_LENGTH = 1024;
// Valor de `utm_via` que marca los UTM rescatados por esta vía
const PROTOCTX_MARKER = "pctx";
// Valor de `utm_via` que marca la atribución resuelta por la regla de Whop
// Ads (ver _searchWhopInUrl). NO es un rescate: Whop manda SIEMPRE su propio
// protoctx, y contarlo como pctx inflaría la métrica del rescate.
const WHOP_MARKER = "whop";
// Procedencias que un protoctx REENVIADO por decorateUrl (extensión `via`)
// conserva en el salto de página. Cualquier otro valor (`fwd`, desconocidos)
// da la atribución sin marcador.
const FORWARDED_VIA_MARKERS = [PROTOCTX_MARKER, WHOP_MARKER];
// El ORDEN es el contrato. Ojo: utm_content va ANTES que utm_term.
const PROTOCTX_FIELDS: (keyof UTMValues)[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
];

/**
 * protoctx v2 (5/8/2026): núcleo posicional (posiciones 1-6, intactas — los
 * parsers desplegados ignoran posiciones sobrantes, verificado) + EXTENSIONES
 * etiquetadas `clave:valor` de la posición 7 en adelante:
 *
 *   src~~med~~camp~~cont~~term~~id~~vid:018f3a2b-...~~<futuras>
 *
 * Claves definidas: `vid` (visitor id, uuidv7 — la identidad viaja con el
 * salto de página para los usuarios sin storage). Reservadas para el futuro:
 * `h` (checksum de integridad), `spl` (asignación de split). Una clave
 * desconocida se conserva en el mapa (forward-compat); una posición sin `:`
 * se ignora en silencio (igual que hacía v1 con las sobrantes).
 */
const PROTOCTX_VID_KEY = "vid";
// Procedencia de la atribución REENVIADA: decorateUrl la adjunta para que el
// marcador pctx sobreviva los saltos sin falsos positivos — un ctx con
// vid/via lo generó decorateUrl (forwarding); uno sin extensiones lo escribió
// el media buyer (candidato a rescate). Ver _searchProtoCtxInUrl.
const PROTOCTX_VIA_KEY = "via";
// Saneado del generador: un valor UTM jamás puede contener el separador (una
// landing con ?utm_campaign=a~~b desplazaría el contrato posicional en el
// siguiente salto e incluso podría inyectar una extensión vid falsa) ni ser
// arbitrariamente largo (60 chars/campo garantiza que lo generado quepa
// siempre bajo el límite de 512 del parser v1 aún desplegado).
const PROTOCTX_MAX_FIELD_LENGTH = 60;

/**
 * Whop Ads — regla de atribución (26/9/2026).
 *
 * Whop compra en Meta desde cuentas de agencia PROPIAS y en cada clic añade a
 * la URL params RESERVADOS que el anunciante no puede tocar: `utm_source`
 * (fb|ig|msg|an), `utm_medium` (paid_social...), `utm_content` (el NOMBRE del
 * anuncio), `utm_adset`, `utm_placement`, `utm_whop=true`, los ids de Whop
 * `wacid` (campaña, `adcamp_...`), `wasid` (grupo, `adgrp_...`) y `waid`
 * (anuncio, `ad_...`), `utm_meta_*`, `tw_*`; Meta añade además `fbclid`.
 *
 * El CRM atribuye la agencia por `utm_source` y canal/campaña/anuncio por
 * `utm_medium` + la convención del Recommended setup (`utm_campaign=c<id>`,
 * `utm_content=a<id>`, `utm_term=s<id>`), así que tal cual llega el lead
 * entra sin atribuir. Decisión con producto: el media buyer añade UN param
 * estático a cada anuncio de Whop, `protoctx=<código de agencia>~~<medium>`
 * (el contrato de siempre, posiciones 3-6 vacías), y aquí se rellenan
 * campaña/anuncio/conjunto con los ids de Whop.
 *
 * Los ids se leen de la query LOWERCASEADA: el import de Whop del backend los
 * guarda en minúsculas y reporting cruza distinguiendo mayúsculas.
 */
const WHOP_TRAFFIC_PARAM = "utm_whop";
const WHOP_ID_OVERRIDES: {
  param: string;
  field: keyof UTMValues;
  prefix: string;
}[] = [
  { param: "wacid", field: "utm_campaign", prefix: "c" },
  { param: "waid", field: "utm_content", prefix: "a" },
  { param: "wasid", field: "utm_term", prefix: "s" },
];

interface ProtoCtxParseResult {
  // Los 6 UTM cuando el valor cumple el contrato; null si no se puede usar
  utm: UTMValues | null;
  // Extensiones etiquetadas de las posiciones 7+ ({} si no hay o inválido)
  extensions: Record<string, string>;
  // Motivo del descarte, para telemetría ("" cuando es válido)
  reason: string;
}

/**
 * Parsea el valor de `protoctx` a UTMs. NUNCA lanza: el init del collector no
 * puede romperse por un parámetro basura (una excepción aquí dejaría la página
 * entera sin analytics ni leads).
 *
 * Válido = `utm_source` y `utm_medium` no vacíos — el mismo requisito que ya
 * exige `_searchUrlParamsInUrl` para dar por buenas las UTMs de la URL.
 * Menos de 6 posiciones → las que falten quedan ""; más de 6 → se ignoran.
 */
function parseProtoCtx(raw: string | null | undefined): ProtoCtxParseResult {
  const empty: UTMValues = {
    utm_source: "",
    utm_campaign: "",
    utm_medium: "",
    utm_term: "",
    utm_content: "",
    utm_id: "",
  };

  try {
    if (typeof raw !== "string") {
      return { utm: null, extensions: {}, reason: "absent" };
    }

    const value = raw.trim();

    if (value === "") {
      return { utm: null, extensions: {}, reason: "empty" };
    }

    if (value.length > PROTOCTX_MAX_LENGTH) {
      return { utm: null, extensions: {}, reason: "too_long" };
    }

    const parts = value.split(PROTOCTX_SEPARATOR);
    const utm: UTMValues = { ...empty };

    for (let index = 0; index < PROTOCTX_FIELDS.length; index++) {
      const part = parts[index];
      utm[PROTOCTX_FIELDS[index]] = typeof part === "string" ? part.trim() : "";
    }

    // Posiciones 7+: extensiones `clave:valor` (v2). Sin `:` o con clave/valor
    // vacíos → se ignora, que es exactamente lo que v1 hacía con las
    // posiciones sobrantes: un protoctx v1 con basura extra sigue parseando
    // idéntico.
    const extensions: Record<string, string> = {};
    for (let index = PROTOCTX_FIELDS.length; index < parts.length; index++) {
      const part = typeof parts[index] === "string" ? parts[index].trim() : "";
      const colonAt = part.indexOf(":");
      if (colonAt <= 0) {
        continue;
      }
      const key = part.slice(0, colonAt).trim();
      const extensionValue = part.slice(colonAt + 1).trim();
      if (key === "" || extensionValue === "") {
        continue;
      }
      extensions[key] = extensionValue;
    }

    if (parts.length < 2) {
      // Ni un separador: o no es nuestro contrato o llegó truncado
      return { utm: null, extensions: {}, reason: "no_separator" };
    }

    if (utm.utm_source === "") {
      return { utm: null, extensions, reason: "missing_source" };
    }

    if (utm.utm_medium === "") {
      return { utm: null, extensions, reason: "missing_medium" };
    }

    return { utm, extensions, reason: "" };
  } catch {
    return { utm: null, extensions: {}, reason: "exception" };
  }
}

/**
 * Serializa protoctx v2 desde estado: 6 posiciones UTM + extensiones
 * etiquetadas. Forma canónica = valores trim + minúsculas (el parser lee la
 * query lowercaseada, así que generar en minúsculas hace el roundtrip
 * idéntico byte a byte). Nunca lanza: ante cualquier problema devuelve "".
 */
function buildProtoCtx(
  utm: UTMValues,
  extensions: Record<string, string> = {},
): string {
  try {
    const positional = PROTOCTX_FIELDS.map((field) =>
      sanitizeProtoCtxValue(utm[field]),
    );

    const tagged: string[] = [];
    for (const key of Object.keys(extensions)) {
      const value = sanitizeProtoCtxValue(extensions[key]);
      const cleanKey = sanitizeProtoCtxValue(key);
      if (value !== "" && cleanKey !== "") {
        tagged.push(cleanKey + ":" + value);
      }
    }

    return positional.concat(tagged).join(PROTOCTX_SEPARATOR);
  } catch {
    return "";
  }
}

// Forma canónica de un valor dentro de protoctx: trim + minúsculas (la query
// se lee lowercaseada), SIN el separador (colapsado a ~ simple — evita el
// desplazamiento posicional y la inyección de extensiones) y con tope por
// campo (garantiza generado <512, el límite del parser v1 aún desplegado).
function sanitizeProtoCtxValue(raw: unknown): string {
  if (typeof raw !== "string") {
    return "";
  }
  let value = raw.trim().toLowerCase();
  while (value.indexOf(PROTOCTX_SEPARATOR) !== -1) {
    value = value.split(PROTOCTX_SEPARATOR).join("~");
  }
  return value.slice(0, PROTOCTX_MAX_FIELD_LENGTH);
}

/**
 * Lee una extensión de protoctx directamente de un query string, sin
 * necesidad de instanciar Tracking. La usa User en el init (que corre ANTES
 * de que Tracking exista) para la adopción del visitor id. Nunca lanza.
 */
function readProtoCtxExtension(search: string, key: string): string {
  try {
    const raw = new URLSearchParams(
      String(search || "").toLowerCase(),
    ).get(PROTOCTX_PARAM);
    const { extensions } = parseProtoCtx(raw);
    return extensions[key] || "";
  } catch {
    return "";
  }
}

// Un ctx con vid/via lo generó decorateUrl (forwarding de la atribución de la
// landing por el funnel); uno sin extensiones lo escribió el media buyer.
function isForwardedProtoCtx(extensions: Record<string, string>): boolean {
  return (
    typeof extensions[PROTOCTX_VID_KEY] === "string" ||
    typeof extensions[PROTOCTX_VIA_KEY] === "string"
  );
}

class Tracking {
  private _scrubRules: ScrubRule[];

  private _currentUtm: StoredUTMValues = {
    utm_source: "",
    utm_campaign: "",
    utm_medium: "",
    utm_term: "",
    utm_content: "",
  };

  constructor(
    scrubRules: ScrubRule[],
    currentExtraParams: ExtraParamsValues,
    utils: Utils,
  ) {
    this._scrubRules = scrubRules;
    const utmFromStorage: StoredUTMValues | false = Vault.get("t_utm_values");

    // Cadena de resolución: Whop → URL → protoctx → storage → org/direct.
    // `protoctx` va POR ENCIMA del storage: es dato del clic ACTUAL, mientras
    // que el storage puede venir de una visita de hace meses. Whop va el
    // primero porque sus `utm_*` son reservados de Whop, no del anunciante.
    if (
      !this._searchWhopInUrl() &&
      !this._searchUrlParamsInUrl() &&
      !this._searchProtoCtxInUrl()
    ) {
      //Ejecutamos la función de buscar en la url los UTM's,
      //si no se detectan, avanza para buscarlo del localstorage.

      if (
        utmFromStorage === false ||
        utmFromStorage.utm_source === "" ||
        utmFromStorage.utm_medium === ""
      ) {
        this._currentUtm = {
          utm_source: "org",
          utm_medium: "direct",
          utm_campaign: "non-utm",
          utm_term: "",
          utm_content: "",
          utm_id: "",
        };
      } else {
        // Hereda también `utm_via`: si estos UTM se rescataron de un protoctx
        // en la landing, las páginas siguientes del funnel siguen marcadas
        this._currentUtm = utmFromStorage;
      }
    }

    // Comprobar si los objetos son diferentes antes de guardar
    if (JSON.stringify(this._currentUtm) !== JSON.stringify(utmFromStorage)) {
      this._save();
    }

    this._resetUrl();

    const funnelStep = this._getFunnelStep();
    let userData: UserData = {
      ...this._currentUtm,
      ...currentExtraParams,
      url: window.location.href,
      referrer: document.referrer,
      funnel_step: funnelStep,
    };

    const formData = Vault.get("u_form_data");
    if (formData !== false) {
      userData = {
        ...userData,
        ...JSON.parse(atob(formData)),
      };
    }

    const scrub = new Scrub(this._scrubRules, utils, currentExtraParams);
    scrub.applyScrub(userData);
  }

  /**
   * Procedencia de la atribución vigente: "pctx" cuando los UTM se rescataron
   * del parámetro `protoctx` (en esta página o en una anterior del funnel, vía
   * storage), "whop" cuando los resolvió la regla de Whop Ads (ídem), "" cuando
   * vinieron como `utm_*` en la URL o son org/direct.
   *
   * Se lee de la copia EN MEMORIA (no del storage) por el mismo motivo que
   * getAdditionalDataToMessage cae a ella: el localStorage puede purgarse en
   * mitad de la sesión. El consumidor es WebAnalyticsEventStructure, que lo
   * adjunta como `utm_via` dentro de `custom_data` — sin él no se puede medir
   * el efecto de esta historia ni separar "el sufijo llegó entero" de
   * "rescatado por protoctx".
   */
  get attributionVia(): string {
    return typeof this._currentUtm.utm_via === "string"
      ? this._currentUtm.utm_via
      : "";
  }

  getAdditionalDataToMessage(): UTMValues {
    // El localStorage puede purgarse EN MITAD de la sesión (WKWebView/ITP,
    // hallazgo 31/7/2026): si la clave no está, caer a la copia en memoria
    // que el constructor deja siempre poblada — antes el destructure de
    // `false` devolvía todo vacío y el lead entraba al CRM sin canal
    // (candidato al 14,6% de optins sin atribución).
    let stored = Vault.get("t_utm_values") as UTMValues | false;
    if (!stored || !stored.utm_source) {
      stored = this._currentUtm;
      reportStorageFallback("tracking", "memory");
    }
    const {
      utm_medium,
      utm_content,
      utm_source,
      utm_id,
      utm_term,
      utm_campaign,
    } = stored;

    return {
      utm_medium: utm_medium || "",
      utm_content: utm_content || "",
      utm_source: utm_source || "",
      utm_id: utm_id || "",
      utm_term: utm_term || "",
      utm_campaign: utm_campaign || "",
    };
  }

  private _getFunnelStep() {
    const url = window.location.href;
    if (url.includes("webinar")) {
      return FunnelStepType.Webinar;
    } else if (url.includes("gracias") || url.includes("thank")) {
      return FunnelStepType.ThankYou;
    } else if (url.includes("direct_book")) {
      return FunnelStepType.DirectBooking;
    } else {
      return FunnelStepType.Landing;
    }
  }

  private _getUrlParameterObject(lowercase = true): URLSearchParams {
    const queryString = lowercase
      ? window.location.search.toLowerCase()
      : window.location.search;
    return new URLSearchParams(queryString);
  }

  /**
   * Primer eslabón de la cadena: tráfico de Whop Ads (`utm_whop=true` en la
   * query lowercaseada). Ver el bloque de WHOP_TRAFFIC_PARAM.
   *
   * Con el protoctx del buyer válido: sus UTMs, pisando campaña/anuncio/
   * conjunto con `c<wacid>` / `a<waid>` / `s<wasid>` (cada uno solo si el
   * param viene y no está vacío; `utm_id`, el que traiga el protoctx) y
   * marcador `whop`.
   *
   * Sin protoctx utilizable: aviso `whop_protoctx_missing` y la cadena sigue
   * EXACTAMENTE como hoy — ganan los `utm_*` de Whop y el lead entra sin
   * atribuir en el CRM (degradación aceptada con producto).
   *
   * Un protoctx REENVIADO (páginas 2+ del funnel) no se toca aquí: no es el
   * del buyer sino la atribución de la landing viajando, y la resuelve la
   * cadena normal (_searchProtoCtxInUrl propaga via:whop). Así la página 2
   * resuelve lo MISMO sobreviva o no `utm_whop` en la query, y el forwarding
   * de una landing de Whop sin protoctx no se convierte en atribución de
   * Whop a mitad de funnel.
   */
  private _searchWhopInUrl(): boolean {
    try {
      const params = this._getUrlParameterObject();
      if (params.get(WHOP_TRAFFIC_PARAM) !== "true") {
        return false;
      }

      const { utm, extensions, reason } = parseProtoCtx(
        params.get(PROTOCTX_PARAM),
      );
      if (utm === null) {
        reportWhopProtoCtxMissing(reason);
        return false;
      }
      if (isForwardedProtoCtx(extensions)) {
        return false;
      }

      const whopUtm: StoredUTMValues = { ...utm, utm_via: WHOP_MARKER };
      for (const { param, field, prefix } of WHOP_ID_OVERRIDES) {
        const id = (params.get(param) || "").trim();
        if (id !== "") {
          whopUtm[field] = prefix + id;
        }
      }
      this._currentUtm = whopUtm;
      return true;
    } catch {
      reportWhopProtoCtxMissing("exception");
      return false;
    }
  }

  private _searchUrlParamsInUrl(): boolean {
    const getParameters = this._getUrlParameterObject();
    const utmUrl: UTMValues = {
      utm_source: getParameters.get("utm_source"),
      utm_campaign: getParameters.get("utm_campaign"),
      utm_medium: getParameters.get("utm_medium"),
      utm_term: getParameters.get("utm_term"),
      utm_content: getParameters.get("utm_content"),
      utm_id: getParameters.get("utm_id"),
    };
    if (
      utmUrl.utm_source === false ||
      utmUrl.utm_medium === false ||
      utmUrl.utm_source == null ||
      utmUrl.utm_medium == null
    ) {
      return false;
    } else {
      for (const property in utmUrl) {
        if (utmUrl[property] === null) {
          utmUrl[property] = "";
        }
      }
      this._currentUtm = utmUrl;
      return true;
    }
  }

  /**
   * Segundo eslabón de la cadena: sin `utm_*` en la URL, intenta recuperarlas
   * del parámetro empaquetado `protoctx` antes de mirar el storage.
   *
   * Se lee de la query LOWERCASEADA (igual que los `utm_*`): los valores
   * llegan en minúsculas, que es como MIG emite sus UTMs. Si algún día el case
   * de `a{creative}` importara, habría que leer de la query cruda como hace
   * ClickIds.ts.
   *
   * Todo el camino va en try/catch: un `protoctx` inválido nunca puede tumbar
   * el init — se ignora, se cuenta y la cadena sigue a storage → org/direct.
   */
  private _searchProtoCtxInUrl(): boolean {
    try {
      const raw = this._getUrlParameterObject().get(PROTOCTX_PARAM);
      if (raw === null) {
        return false;
      }

      const { utm, extensions, reason } = parseProtoCtx(raw);
      if (utm === null) {
        // Llegó pero no se puede usar: nos dirá si Google lo trunca o mutila
        // en vez de eliminarlo entero
        reportProtoCtxInvalid(reason);
        return false;
      }

      // `pctx` mide RESCATES: UTMs que el sufijo del anuncio salvó cuando la
      // superficie del clic se comió los utm_*. Un protoctx REENVIADO por
      // decorateUrl (lleva vid/via — el buyer nunca las escribe) no es un
      // rescate: es la atribución de la landing viajando por el funnel, y
      // marcarla pctx inflaría la métrica a toda sesión multipágina
      // (hallazgo de la revisión 6/8/2026). En forwarding, la procedencia
      // real viaja en la extensión `via` y se propaga tal cual si es una de
      // las conocidas (pctx, whop — la de Whop llega aquí en las páginas 2+
      // del funnel, con o sin `utm_whop` en la query); en un ctx del buyer
      // (sin extensiones), marcador pctx como siempre.
      if (isForwardedProtoCtx(extensions)) {
        const forwardedVia = extensions[PROTOCTX_VIA_KEY];
        this._currentUtm =
          FORWARDED_VIA_MARKERS.indexOf(forwardedVia) !== -1
            ? { ...utm, utm_via: forwardedVia }
            : { ...utm };
      } else {
        // Belt: un buyer jamás escribe source=org — si llega, no es rescate
        this._currentUtm =
          utm.utm_source === "org"
            ? { ...utm }
            : { ...utm, utm_via: PROTOCTX_MARKER };
      }
      return true;
    } catch {
      reportProtoCtxInvalid("exception");
      return false;
    }
  }

  private _save(): void {
    Vault.set("t_utm_values", this._currentUtm, { expires: "365d" });
  }

  // Reescribe en la barra el protoctx sin su extensión `vid` (identidad ya
  // adoptada). Conserva UTMs y demás extensiones (`via` incluida). Trabaja
  // sobre el objeto de params que _resetUrl va a serializar; el nombre del
  // param puede venir en cualquier case (los lectores lowercasean).
  private _stripVidFromBarProtoCtx(params: URLSearchParams): void {
    try {
      for (const name of Array.from(params.keys())) {
        if (name.toLowerCase() !== PROTOCTX_PARAM) {
          continue;
        }
        const { utm, extensions } = parseProtoCtx(params.get(name));
        if (utm === null || typeof extensions[PROTOCTX_VID_KEY] !== "string") {
          continue;
        }
        delete extensions[PROTOCTX_VID_KEY];
        params.delete(name);
        params.set(PROTOCTX_PARAM, buildProtoCtx(utm, extensions));
      }
    } catch {
      // dejar la barra como estaba antes que romper el init
    }
  }

  private _resetUrl(): void {
    const urlSearchStringObject = this._getUrlParameterObject(false);
    // Solo los NOMBRES de _currentUtm (los 6 UTM + utm_via), nunca un
    // barrido de `utm_*`: `utm_whop`, `wacid`, `wasid` y `waid` se quedan en
    // la barra porque los lee el píxel de Whop (y viajan a la página 2 en el
    // passthrough de decorateUrl).
    const utmParametersNameToBeRemoved = Object.getOwnPropertyNames(
      this._currentUtm,
    );

    for (const utm of utmParametersNameToBeRemoved) {
      urlSearchStringObject.delete(utm);
    }

    // `protoctx` NO se limpia — igual que los click ids (gclid/gad_*/fbclid),
    // que tampoco se tocan porque los pixels de las redes los leen en el load
    // (ver nota en ClickIds.ts).
    //
    // La primera versión sí lo borraba, por simetría con los `utm_*`. Medido en
    // prod el 4/8/2026: borrarlo se llevó por delante la atribución de la
    // SEGUNDA página del funnel (`/video`), que pasó de ~2% de `org` a ~18% en
    // los 20 min siguientes al deploy — peor que el 8,3% que esta historia
    // venía a arreglar. La landing no se movió (~2% estable). El motivo es que
    // las páginas del funnel se pasan el query string entre ellas: `protoctx`
    // es lo único con atribución que sobrevive al salto, porque los `utm_*` los
    // borra esta misma función en la landing. Al quitarlo de la barra, la
    // página 2 se quedaba sin nada que leer y dependía solo del storage — que
    // es justo lo que falla en la cohorte que originó todo esto (Custom Tabs de
    // Android, camino ITP de Safari).
    //
    // Coste de dejarlo: `protoctx` se queda visible en la barra y llega a
    // `web_event.url`. A cambio, las columnas `ctx_ok`/`ctx_roto`/`org_ctx` del
    // report de attribution-integrity siguen siendo medibles.
    //
    // MATIZ v2 (revisión 6/8/2026): la extensión `vid` SÍ se retira de la
    // barra una vez leída (User ya adoptó en el init, que corre antes que
    // este constructor). Dejarla haría `web_event.url` único POR VISITANTE
    // (rompe cualquier agrupación por URL en ClickHouse) y filtraría el
    // visitor id como page_location a GA4/Meta. Las UTMs del ctx se quedan:
    // son las que las páginas legacy se re-pasan a mano.
    this._stripVidFromBarProtoCtx(urlSearchStringObject);

    const path = location.pathname,
      searchSeparator = urlSearchStringObject.toString() !== "" ? "?" : "";

    const newUrl =
      path + searchSeparator + urlSearchStringObject.toString() + location.hash;
    window.history.replaceState({}, "", newUrl);
  }
}
